import { FC, useMemo, useContext, useState } from 'react';
import {
  Alert,
  Dialog,
  Flex,
  Button,
  ButtonGroup,
  DraggableQuestionnaireItems,
  Checkbox,
  type SelectItem,
} from '@/_components';
import type { RequestAdditionalItemsDialogProps } from './RequestAdditionalItemsDialog.types';
import { sendOpenItems } from '@/_api/admin-questionnaires';
import { addPriorYearItem } from '@/_api/admin-prior-year-items';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { useItemList } from '@/_hooks/useItemList';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { processMessageText } from '@/_utils/strings';

export const RequestAdditionalItemsDialog: FC<
  RequestAdditionalItemsDialogProps
> = ({ isOpen, onClose, questionnaire, reloadQuestionnairePreview }) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showRolloverConfirmation, setShowRolloverConfirmation] =
    useState<boolean>(false);
  const [pendingQuestions, setPendingQuestions] = useState<any[]>([]);
  const [selectedRolloverItems, setSelectedRolloverItems] = useState<
    Set<number>
  >(new Set());

  const {
    items,
    addItem,
    removeItem,
    updateItem,
    reorderItems,
    resetItems,
    getItems,
  } = useItemList();

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);

  // All sections (include non-triggered sections)
  const availableSections: Array<SelectItem> = useMemo(() => {
    const additionalItemsSection = {
      name: 'Additional Items',
      value: '0',
    };
    const nonAdditionalItemsSections =
      questionnaire?.sections
        ?.filter((s) => s.section_type === 3 && s.name !== 'Additional Items')
        .map((s) => ({
          name: s.name,
          value: s.id.toString(),
        })) || [];

    return [...nonAdditionalItemsSections, additionalItemsSection];
  }, [questionnaire?.sections]);

  const onRequestAdditionalItems = async (event: React.FormEvent) => {
    event.preventDefault();

    const questionsToCreate = getItems().map((item) => {
      const isUpload = item.question_type === 'upload';

      // Sanitize text to remove non-breaking spaces from QuillJS bug (temp patch for https://github.com/slab/quill/issues/4535)
      // Also watch: https://github.com/slab/quill/issues/4509
      const sanitizedText = processMessageText(item.text);

      return {
        options: isUpload ? [sanitizedText] : [],
        section_id: item.section_id,
        question_type: item.question_type,
        text:
          isUpload && !firm?.preferences.remove_please_upload_for_rollover_items
            ? `Please upload ${sanitizedText}`
            : sanitizedText,
        originalText: sanitizedText, // Store original text for rollover items
      };
    });

    // Check if there are any upload items
    const uploadItems = questionsToCreate.filter(
      (item) => item.question_type === 'upload',
    );

    if (uploadItems.length > 0 && firm?.id === 633) {
      // Show confirmation dialog for rollover items
      setPendingQuestions(questionsToCreate);
      // Initialize all upload items as selected by default
      const uploadItemIndices = questionsToCreate
        .map((item, index) => (item.question_type === 'upload' ? index : -1))
        .filter((index) => index !== -1);
      setSelectedRolloverItems(new Set(uploadItemIndices));
      setShowRolloverConfirmation(true);
      return;
    }

    // No upload items, proceed directly
    await submitQuestions(questionsToCreate);
  };

  const submitQuestions = async (questionsToCreate: any[]) => {
    setLoading(true);

    const params = {
      company_ids: [questionnaire?.company_id],
      due_date: questionnaire?.due_date,
      is_draft: false,
      name: questionnaire?.name,
      relevant_questionnaire_id: questionnaire?.id,
      questions_to_create: questionsToCreate,
      tax_year: questionnaire?.tax_year,
    };

    try {
      await sendOpenItems({ params, token });

      reloadQuestionnairePreview();
      addToast('success', 'Additional items requested');
    } catch (error) {
      addToast('error', '', { error });
    } finally {
      setLoading(false);
      resetItems();
      setShowRolloverConfirmation(false);
      setPendingQuestions([]);
      onClose();
    }
  };

  const handleRolloverConfirmation = async (createRolloverItems: boolean) => {
    if (createRolloverItems && selectedRolloverItems.size > 0) {
      // Create rollover items for selected upload questions only
      const selectedItems = Array.from(selectedRolloverItems)
        .map((index) => pendingQuestions[index])
        .filter((item) => item && item.question_type === 'upload');

      try {
        setLoading(true);

        // Create rollover items
        for (const item of selectedItems) {
          await addPriorYearItem({
            companyId: questionnaire?.company_id!,
            name: item.originalText,
            year: questionnaire?.tax_year!,
            token,
          });
        }

        addToast(
          'success',
          `Created ${selectedItems.length} rollover item(s) for ${questionnaire?.tax_year}`,
        );
      } catch (error) {
        addToast('error', 'Failed to create rollover items', { error });
      } finally {
        setLoading(false);
      }
    }

    // Proceed with submitting questions
    await submitQuestions(pendingQuestions);
  };

  const isValid: boolean = items.every((item) => !!item.text);

  const toggleRolloverItemSelection = (index: number) => {
    const newSelection = new Set(selectedRolloverItems);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedRolloverItems(newSelection);
  };

  const selectedCount = selectedRolloverItems.size;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Request Additional Items"
        isExtraWide={true}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={10}>
            <Alert type="info" icon="info">
              Fill out the following fields to request additional items. Do not
              include slashes (/) or periods (.) for document options. These
              items will be added under &quot;Additional Items&quot; section in
              the questionnaire. The client will receive a notification to
              complete these items.
            </Alert>

            <form onSubmit={onRequestAdditionalItems}>
              <Flex isColumn={true} gap={6} paddingX={8}>
                <DraggableQuestionnaireItems
                  items={items}
                  updateItem={updateItem}
                  removeItem={removeItem}
                  addItem={addItem}
                  onReorder={reorderItems}
                  isLoading={isLoading}
                  addButtonText="Add additional item"
                  availableSections={availableSections}
                />
              </Flex>

              <ButtonGroup align="right">
                <Button
                  value={`${questionnaire?.is_draft ? 'Add' : 'Send'} Additional Items`}
                  iconRight="send"
                  intent="primary"
                  type="submit"
                  isDisabled={!isValid || isLoading}
                  isLoading={isLoading}
                />
              </ButtonGroup>
            </form>
          </Flex>
        </Dialog.Content>
      </Dialog>

      {/* Rollover Items Confirmation Dialog */}
      <Dialog
        isOpen={showRolloverConfirmation}
        onClose={() => setShowRolloverConfirmation(false)}
        title={`Create Rollover Items for ${questionnaire?.tax_year}?`}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={10}>
            <Alert type="info" icon="info">
              Select which upload items you&apos;d like to create as rollover
              items.
            </Alert>

            <Flex isColumn={true} gap={4} yAlign="start">
              <strong>Items to create as rollover items:</strong>
              {pendingQuestions.map((item, index) => {
                if (item.question_type !== 'upload') return null;
                return (
                  <Flex key={index} xAlign="center" gap={8} paddingY={2}>
                    <Checkbox
                      isChecked={selectedRolloverItems.has(index)}
                      onChange={() => toggleRolloverItemSelection(index)}
                      label={item.originalText}
                    />
                  </Flex>
                );
              })}
            </Flex>

            <ButtonGroup align="right">
              <Button
                value="No, just send the request"
                intent="secondary"
                onClick={() => handleRolloverConfirmation(false)}
                isDisabled={isLoading}
              />
              <Button
                value={
                  selectedCount > 0
                    ? `Yes, create ${selectedCount} rollover item${selectedCount > 1 ? 's' : ''}`
                    : 'Yes, create rollover items'
                }
                intent="primary"
                onClick={() => handleRolloverConfirmation(true)}
                isLoading={isLoading}
                isDisabled={isLoading || selectedCount === 0}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
